import { FC, useState, useEffect } from "react";
import { CaretDown } from "@phosphor-icons/react";
import { ReactNode } from "react";

interface MenuItemWithSubmenu {
  id: string;
  label: string;
  icon?: ReactNode;
  link?: string;
  isExternalLink?: boolean;
  subItems?: {
    id: string;
    label: string;
    link: string;
    icon?: ReactNode;
    noCloseMenu?: boolean;
    isExternalLink?: boolean;
  }[];
  noCloseMenu?: boolean;
  highlight?: boolean;
}

interface NavigationMenuCollapsibleProps {
  items: MenuItemWithSubmenu[];
  isNavigationBarCollapsed: boolean;
  onItemClick: (item: any) => void;
  isActiveItem: (link?: string) => boolean;
  onOpenNavBar?: () => void;
  t?: (key: string) => string;
}

const NavigationMenuCollapsible: FC<NavigationMenuCollapsibleProps> = ({
  items,
  isNavigationBarCollapsed,
  onItemClick,
  isActiveItem,
  onOpenNavBar,
}) => {
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});
  const [savedExpandedState, setSavedExpandedState] = useState<Record<string, boolean>>({});

  // When navigation bar collapses, save the expanded state and collapse all
  // When it expands, restore the saved state
  useEffect(() => {
    if (isNavigationBarCollapsed) {
      setSavedExpandedState(expandedItems);
      setExpandedItems({});
    } else {
      setExpandedItems(savedExpandedState);
    }
  }, [isNavigationBarCollapsed]);

  const toggleExpanded = (itemId: string) => {
    setExpandedItems((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };

  const handleItemClick = (itemId: string, hasSubItems: boolean) => {
    // Si el menú está colapsado y tiene subItems, abre el menú primero
    if (isNavigationBarCollapsed && hasSubItems && onOpenNavBar) {
      onOpenNavBar();
      // Expandir el item después de un pequeño delay para que la animación se vea bien
      setTimeout(() => {
        setExpandedItems((prev) => ({
          ...prev,
          [itemId]: true,
        }));
      }, 100);
    } else if (hasSubItems) {
      toggleExpanded(itemId);
    }
  };

  return (
    <ul className="navigation-bar-menu navigation-bar-menu__collapsible">
      {items.map((item) => {
        const hasSubItems = item.subItems && item.subItems.length > 0;
        const isExpanded = expandedItems[item.id];
        const hasActiveSubItem = item.subItems?.some((sub) => isActiveItem(sub.link));

        return (
          <li key={`nav-item-${item.id}`} className="navigation-bar-menu-item navigation-bar-menu-item__collapsible">
            {hasSubItems ? (
              <>
                <button
                  className={`navigation-bar-menu-link navigation-bar-menu-link__collapsible ${
                    isExpanded ? "navigation-bar-menu-link__expanded" : ""
                  } ${hasActiveSubItem ? "navigation-bar-menu-link__active" : ""}`}
                  onClick={() => handleItemClick(item.id, true)}
                >
                  <span className="icon">
                    {item.icon}
                  </span>
                  <span className="label">{item.label}</span>
                  <CaretDown weight="bold" size={14} className="caret-icon" />
                </button>
                {isExpanded && (
                  <ul className="navigation-bar-submenu">
                    {item.subItems?.map((subItem) => (
                      <li key={`nav-subitem-${subItem.id}`} className="navigation-bar-submenu-item">
                        <button
                          className={`navigation-bar-submenu-link ${
                            isActiveItem(subItem.link) ? "navigation-bar-submenu-link__active" : ""
                          }`}
                          onClick={() => onItemClick({
                            id: subItem.id,
                            name: subItem.label,
                            link: subItem.link,
                            isActive: true,
                            existInApp: true,
                            existInWeb: true,
                            noCloseMenu: subItem.noCloseMenu,
                            isExternalLink: subItem.isExternalLink,
                          })}
                        >
                          {subItem.icon && <span className="icon">{subItem.icon}</span>}
                          <span className="label">{subItem.label}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            ) : (
              <button
                className={`navigation-bar-menu-link ${
                  isActiveItem(item.link ?? (item.id !== "home" ? `/${item.id}` : "/"))
                    ? "navigation-bar-menu-link__active"
                    : ""
                }
                ${item.highlight ? "navigation-bar-menu-link__highlight" : ""}`}
                onClick={() => onItemClick({
                  id: item.id,
                  name: item.label,
                  link: item.link ?? (item.id !== "home" ? `/${item.id}` : "/"),
                  isActive: true,
                  existInApp: true,
                  existInWeb: true,
                  noCloseMenu: item.noCloseMenu,
                  isExternalLink: item.isExternalLink,
                })}
              >
                <span className="icon">
                  {item.icon}
                </span>
                <span className="label">{item.label}</span>
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
};

export default NavigationMenuCollapsible;
